/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { task } from 'ember-concurrency';
import FormValidations from 'cio/utils/form-validations';
import SnippetValidations from 'cio/utils/validations/snippet';

import DS from 'ember-data';
import { htmlSafe } from '@ember/template';
import RouterService from '@ember/routing/router-service';
import ApiErrorsService from 'cio/services/api-errors';
import AuthService from 'cio/services/auth';
import AlertsService from 'cio/services/alerts';
import SnippetModel from 'cio/models/snippet';

export default class NewSnippetPage extends Component {
  @service store!: DS.Store;
  @service alerts!: AlertsService;
  @service auth!: AuthService;
  @service apiErrors!: ApiErrorsService;
  @service router!: RouterService;

  @tracked snippet!: SnippetModel;
  @tracked hasServerErrors = false;

  readonly snippetPretext = htmlSafe('{{snippets.');
  readonly snippetPosttext = htmlSafe('}}');
  validations!: FormValidations;

  get isSaveDisabled() {
    return (
      !this.auth.hasPermission('journeys/snippets/create') || this.validations.hasErrors || !this.validations.isTouched
    );
  }

  @action
  setName(event: KeyboardEvent) {
    this.hasServerErrors = false;
    this.snippet.name = (event.target as HTMLInputElement).value;
    this.validations.validateAttribute('name');
  }

  @action
  setValue(value: string) {
    this.hasServerErrors = false;
    this.snippet.value = value;
    this.validations.validateAttribute('value');
  }

  saveTask = task(async () => {
    this.hasServerErrors = false;

    this.snippet.name = this.snippet.name?.trim();
    this.snippet.value = this.snippet.value?.trim();

    try {
      await this.validations.validateModel();
    } catch (e) {
      // `validateModel` already found and set validation errors
      // return early to avoid executing real submit logic
      return false;
    }

    try {
      let snippet = await this.snippet.save();

      this.alerts.success('snippet.create.success', 'Snippet created');
      this.router.transitionTo('snippets.snippet', this.snippet.id);

      return snippet;
    } catch (reason) {
      this.hasServerErrors = true;

      if (reason.errors?.length && reason.errors[0].source?.pointer) {
        // The back end will handle some validation errors by itself, so we don't need to report those
        // to the apiErrors service. FormValidations and ember data handle the UI for us, conveniently.
        return false;
      }

      this.apiErrors.report(reason);

      return false;
    }
  });

  constructor(owner: unknown, args: Record<string, unknown>) {
    super(owner, args);

    this.snippet = this.store.createRecord('snippet');
    this.validations = FormValidations.create({
      validations: SnippetValidations,
      context: this,
      modelKey: 'snippet',
    });
  }

  willDestroy() {
    super.willDestroy();

    // unload record if not saved:
    if (this.snippet.get('isNew')) {
      this.snippet.unloadRecord();
    }
  }
}
